import { http, id } from 'utils'
import { SET_PLACEMENTS, SET_PLACEMENT, SET_PROPERTY } from 'vx/mutations'
import { set, map, groupBy, first, get } from 'lodash'
import performanceObjectives from './placements/performance-objectives'
import developmentObjectives from './placements/development-objectives'
import capabilities from './placements/capabilities'

const modules = {
  performanceObjectives,
  developmentObjectives,
  capabilities
}

export const initialPlacementState = () => ({
  number: null,
  start_date: null,
  end_date: null,
  start_component_open_date: null,
  start_component_due_date: null,
  end_component_open_date: null,
  end_component_due_date: null,
  region: null,
  email: null,
  finalised: null,
  mid_point_checkin: {
    date_complete: null,
    feedback: null
  }
})

const state = {
  placement: initialPlacementState(),
  placements: []
}

// Placement state getters.
const getters = {
  placementExists: (state) => state.placement && state.placement.id !== null && state.placement.id !== undefined,
  placementStartComponent: (state) => state.placement ? state.placement.start_component : null,
  placementEndComponent: (state) => state.placement ? state.placement.end_component : null,
  groupedCapabilities: (state) => {
    return map(groupBy(state.placement.start_capabilities, 'group_id'), function (capabilities) {
      return {
        id: first(capabilities).group_id,
        name: first(capabilities).group_name,
        icon: first(capabilities).group_icon,
        capabilities
      }
    })
  },
  strengthText: state => get(state, 'placement.is_starting_2021', false)
    ? 'Strength'
    : 'Likely to be a strength',

  placementMidPointCheckin: (state) => {
    return (state.placement && state.placement.mid_point_checkin)
      ? state.placement.mid_point_checkin
      : { date_complete: null, feedback: null }
  }

}

// Placement actions
const actions = {
  /**
   * Get a logged in user's (graduate) placements.
   *
   * @return Promise
   */
  async getPlacements ({ commit }, params = {}) {
    commit(SET_PLACEMENTS, [])

    const response = await http.get('placements', { params })
    commit(SET_PLACEMENTS, response.data.data)

    return response
  },

  /**
   * Get a single placement for the given graduate.
   *
   * @return Promise
   */
  async getPlacement ({ commit }, { graduate, placement }) {
    const response = await http.get(`/graduates/${id(graduate)}/placements/${id(placement)}`)

    commit(SET_PLACEMENT, response.data.data)

    return response
  },

  async updateAssignments ({ commit }, { graduate, placement, payload }) {
    const response = await http.post(`/graduates/${id(graduate)}/placements/${id(placement)}/assignments`, payload)

    return response
  },

  async updatePlacement ({ commit }, { graduate, placement, payload }) {
    const response = await http.put(`/graduates/${id(graduate)}/placements/${id(placement)}`, payload)

    return response
  },

  async acknowledgeCapabilitiesRequiringDevelopment ({ commit }, { graduate, placement }) {
    const response = await http.post(`/graduates/${id(graduate)}/placements/${id(placement)}/acknowledge-requiring-development`)

    return response
  },

  async updateEndPlacementComponent ({ commit }, { graduate, placement, payload }) {
    const response = await http.post(`/graduates/${id(graduate)}/placements/${id(placement)}/end-component/objectives-review`, payload)

    return response
  },

  async approveStartComponent ({ commit }, { graduate, placement }) {
    const response = await http.post(`/graduates/${id(graduate)}/placements/${id(placement)}/start-component/approve`)

    return response
  },

  async getStartPlacementComments ({ commit }, { graduate, placement }) {
    const response = await http.get(`/graduates/${id(graduate)}/placements/${id(placement)}/start-component/comments`)

    return response
  },

  async addStartPlacementComment ({ commit }, { graduate, placement, payload }) {
    const response = await http.post(`/graduates/${id(graduate)}/placements/${id(placement)}/start-component/comments`, payload)

    return response
  },

  async approveEndComponent ({ commit }, { graduate, placement }) {
    const response = await http.post(`/graduates/${id(graduate)}/placements/${id(placement)}/end-component/approve`)

    return response
  },

  async revertEndComponent ({ commit }, { graduate, placement }) {
    const response = await http.post(`/graduates/${id(graduate)}/placements/${id(placement)}/end-component/revert`)

    return response
  },

  async storeOverallAssessment ({ commit }, { graduate, placement, payload }) {
    const response = await http.post(`/graduates/${id(graduate)}/placements/${id(placement)}/end-component/assessment`, payload)

    return response
  },

  async storeMidPointCheckin ({ commit }, { graduate, placement, payload }) {
    const response = await http.post(`/graduates/${id(graduate)}/placements/${id(placement)}/mid-point-checkin`, payload)

    return response
  },

  getPlacementPdp (store, { graduate, placement }) {
    return http.get(`/graduates/${id(graduate)}/placements/${id(placement)}/pdp`, { responseType: 'blob' })
  },

  setPlacementProperty ({ commit }, payload) {
    commit(SET_PROPERTY, payload)
  },

  resetPlacement ({ commit }) {
    commit(SET_PLACEMENT, initialPlacementState())
  }
}

// Placement state mutations.
const mutations = {
  [SET_PLACEMENTS] (state, placements) {
    state.placements = placements
  },

  [SET_PLACEMENT] (state, placement) {
    state.placement = placement
  },

  [SET_PROPERTY] (state, { property, value }) {
    set(state.placement, property, value)
  }
}
// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations, modules
}
